/** Primary production backend. Media assets are returned as S3 URLs. */
export const PRODUCTION_API_BASE = 'https://api.snapsule.in/api';

/** Render backend — reliable when VPS socket/nginx has issues. */
export const RENDER_API_BASE = 'https://sanpsule-1.onrender.com/api';

function isProductionFrontendHost(hostname) {
  return (
    hostname === 'snapsule.in' ||
    hostname === 'www.snapsule.in' ||
    hostname.endsWith('.onrender.com')
  );
}

function isLocalDevHost(hostname) {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(hostname)
  );
}

function isNativeAppShell() {
  if (typeof window === 'undefined') return false;
  if (window.Capacitor?.isNativePlatform?.()) return true;
  const protocol = window.location?.protocol || '';
  return protocol === 'capacitor:' || protocol === 'ionic:' || protocol === 'file:';
}

/**
 * Resolve the backend API base URL.
 * Production domains always use api.snapsule.in (never hostname:5000).
 * Local/LAN dev uses the backend on port 5000.
 * Native app shells use REACT_APP_API_URL (Capacitor reports localhost).
 */
export function getApiBaseUrl() {
  if (typeof window !== 'undefined') {
    const { hostname, protocol } = window.location;

    if (isNativeAppShell() && process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }

    if (hostname.endsWith('.onrender.com')) {
      return RENDER_API_BASE;
    }

    if (hostname === 'snapsule.in' || hostname === 'www.snapsule.in') {
      return PRODUCTION_API_BASE;
    }

    if (isLocalDevHost(hostname)) {
      if (
        process.env.REACT_APP_API_URL &&
        process.env.NODE_ENV === 'production' &&
        hostname === 'localhost'
      ) {
        return process.env.REACT_APP_API_URL;
      }
      return `${protocol}//${hostname}:5000/api`;
    }
  }

  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  return 'http://localhost:5000/api';
}

export function getApiOrigin() {
  return getApiBaseUrl().replace(/\/api\/?$/, '');
}

function encodeUploadPath(pathname) {
  return pathname
    .split('/')
    .map((segment) => (segment ? encodeURIComponent(segment) : ''))
    .join('/');
}

function resolveLegacyUploadPath(mediaUrl) {
  const value = String(mediaUrl || '').trim();
  if (!value) return '';

  let pathname = '';
  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      pathname = parsed.pathname || '';
    } catch {
      return '';
    }
  } else if (value.startsWith('/')) {
    pathname = value;
  } else if (value.startsWith('uploads/')) {
    pathname = `/${value}`;
  }

  if (!pathname.startsWith('/uploads/')) return '';

  return `${getApiOrigin()}${encodeUploadPath(pathname)}`;
}

/**
 * Resolve a media URL for display.
 * - S3 / absolute URLs are returned as-is (legacy localhost upload URLs are rewritten).
 * - Relative `/uploads/...` paths are served from the API origin.
 */
export function getAssetUrl(mediaUrl = '') {
  if (!mediaUrl) return '';
  const trimmed = String(mediaUrl).trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed;

  const legacyUpload = resolveLegacyUploadPath(trimmed);
  if (legacyUpload) return legacyUpload;

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  return trimmed;
}
